import type { ReactNode } from "react";

interface Column<T> {
  key: string;
  header: string;
  align?: "left" | "center" | "right";
  width?: string;
  render: (row: T, index: number) => ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  loading?: boolean;
  minWidth?: string;
}

const ALIGN_MAP = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const SkeletonRows = ({ count, cols }: { count: number; cols: number }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="border-t border-gray-100 animate-pulse">
        {Array.from({ length: cols }).map((__, j) => (
          <td key={j} className="p-3">
            <div className="h-4 bg-gray-100 rounded w-3/4 mx-auto" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export const DataTable = <T,>({
  columns,
  data,
  rowKey,
  empty,
  loading,
  minWidth = "800px",
}: DataTableProps<T>) => {
  return (
    <div className="w-full overflow-x-auto scrollbar-custom">
      <table className="w-full" style={{ minWidth }}>
        <thead className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur">
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2.5 text-[11px] uppercase tracking-wider text-gray-500 font-semibold ${ALIGN_MAP[col.align ?? "left"]}`}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && data.length === 0 ? (
            <SkeletonRows count={5} cols={columns.length} />
          ) : data.length > 0 ? (
            data.map((row, index) => (
              <tr
                key={rowKey(row)}
                className="border-t border-gray-100 hover:bg-blue-50/40 transition-colors"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-3 text-sm text-gray-700 ${ALIGN_MAP[col.align ?? "left"]}`}
                  >
                    {col.render(row, index)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>{empty}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
